import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const QuotePage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(productId || "");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        application: "",
        message: "",
    });
    
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await publicApi.getProducts();
                setProducts(res.data);
            } catch (err) {
                console.error("Failed to fetch products:", err);
            }
        };
        fetchProducts();
    }, []);
    
    useEffect(() => {
        if (productId) {
            setSelectedProduct(productId);
        }
    }, [productId]);
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const product = products.find(p => p.id === selectedProduct);
            const productId = selectedProduct === "general" ? null : selectedProduct;
            await publicApi.submitQuote({
                request_type: "quote",
                product_id: productId || null,
                product_name: product?.display_title || null,
                ...formData
            });
            setSubmitted(true);
            toast.success("Quote request submitted successfully!");
            navigate("/thank-you");
        } catch (err) {
            console.error("Failed to submit quote:", err);
            toast.error("Failed to submit request. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    
    if (submitted) {
        return (
            <div className="noise-overlay min-h-screen flex items-center justify-center">
                <div className="container mx-auto px-6 lg:px-12 max-w-2xl text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Check className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-4" data-testid="quote-success">
                        Quote Request Received
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Thank you for your interest! Our team will review your request and get back to you within 24 hours with pricing and availability.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            onClick={() => navigate("/products")} 
                            variant="outline" 
                            className="rounded-none uppercase font-bold tracking-wider"
                        >
                            Continue Browsing
                        </Button>
                        <Button 
                            onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", company: "", application: "", message: "" }); }}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider"
                        >
                            Submit Another Request
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
    
    const selectedProductData = selectedProduct && selectedProduct !== "general" ? products.find(p => p.id === selectedProduct) : null;
    
    return (
        <div className="noise-overlay min-h-screen">
            {/* Header */}
            <section className="py-12 lg:py-16 border-b border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Get Started</p>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase">
                        Request a Quote
                    </h1>
                    <p className="text-muted-foreground mt-4 max-w-2xl">
                        Ready to move forward? Request pricing and availability for any system in our catalog. We'll respond within 24 hours.
                    </p>
                </div>
            </section>
            
            <div className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-2">
                        <Card className="bg-card border border-border rounded-none">
                            <CardContent className="p-6 lg:p-8">
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Product Selection */}
                                    <div className="space-y-2">
                                        <Label htmlFor="product" className="font-mono text-xs uppercase tracking-wider">
                                            Select Product (Optional)
                                        </Label>
                                        <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                                            <SelectTrigger className="rounded-none bg-input border-transparent focus:border-primary h-12" data-testid="product-select">
                                                <SelectValue placeholder="Select a product..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-card border-border rounded-none">
                                                <SelectItem value="general" className="rounded-none">General Inquiry</SelectItem>
                                                {products.map((product) => (
                                                    <SelectItem key={product.id} value={product.id} className="rounded-none">
                                                        {product.display_title} ({product.internal_name})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="font-mono text-xs uppercase tracking-wider">
                                                Full Name *
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="John Smith"
                                                data-testid="quote-name"
                                            />
                                        </div>
                                        
                                        {/* Email */}
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider">
                                                Email Address *
                                            </Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="john@company.com"
                                                data-testid="quote-email"
                                            />
                                        </div>
                                        
                                        {/* Phone */}
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-wider">
                                                Phone Number
                                            </Label>
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="+1 (555) 123-4567"
                                                data-testid="quote-phone"
                                            />
                                        </div>
                                        
                                        {/* Company */}
                                        <div className="space-y-2">
                                            <Label htmlFor="company" className="font-mono text-xs uppercase tracking-wider">
                                                Company Name
                                            </Label>
                                            <Input
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                                placeholder="ACME Industries"
                                                data-testid="quote-company"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Application */}
                                    <div className="space-y-2">
                                        <Label htmlFor="application" className="font-mono text-xs uppercase tracking-wider">
                                            Application / Use Case
                                        </Label>
                                        <Input
                                            id="application"
                                            name="application"
                                            value={formData.application}
                                            onChange={handleInputChange}
                                            className="rounded-none bg-input border-transparent focus:border-primary h-12"
                                            placeholder="e.g., Automotive detailing, Industrial cleaning"
                                            data-testid="quote-application"
                                        />
                                    </div>
                                    
                                    {/* Message */}
                                    <div className="space-y-2">
                                        <Label htmlFor="message" className="font-mono text-xs uppercase tracking-wider">
                                            Additional Details
                                        </Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={4}
                                            className="rounded-none bg-input border-transparent focus:border-primary resize-none"
                                            placeholder="Tell us about your project, volume requirements, timeline, or any questions..."
                                            data-testid="quote-message"
                                        />
                                    </div>
                                    
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider py-6 text-lg"
                                        data-testid="quote-submit-btn"
                                    >
                                        {loading ? "Submitting..." : "Submit Quote Request"}
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Selected Product Preview */}
                        {selectedProductData && (
                            <Card className="bg-card border border-border rounded-none overflow-hidden">
                                {selectedProductData.images?.[0] && (
                                    <div className="aspect-video">
                                        <img 
                                            src={resolveImageUrl(selectedProductData.images[0])} 
                                            alt={selectedProductData.display_title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                                <CardContent className="p-4">
                                    <p className="font-mono text-xs text-muted-foreground">{selectedProductData.internal_name}</p>
                                    <h3 className="font-heading font-bold">{selectedProductData.display_title}</h3>
                                    <p className="font-mono text-primary font-bold mt-2">{selectedProductData.price_text}</p>
                                </CardContent>
                            </Card>
                        )}
                        
                        {/* What to Expect */}
                        <Card className="bg-secondary/50 border border-border rounded-none">
                            <CardContent className="p-6">
                                <h3 className="font-heading font-bold text-lg mb-4">What to Expect</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Response within 24 hours
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Detailed pricing breakdown
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Shipping and lead time info
                                    </li>
                                    <li className="flex items-start gap-3 text-sm text-muted-foreground">
                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        Available financing options
                                    </li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuotePage;
